import { useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, Send, X, CalendarCheck, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RichText } from "@/components/AllyWidget";
import { AllyLeadCard } from "@/components/AllyLeadCard";
import { getOpener } from "@/lib/allyOpeners";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE = 2;
const KEY = "deferally_public_ally";
const VIDEO_ASK = /\b(demo|video|walk ?through|overview|see (it|the|how)|show me|how (does|do) (it|you|deferally) work|tour)\b/i;
const PRIVATE = /^\/(enterprise|admin|client|dashboard|login|register|forgot-password|reset-password|portal|ap$|sign|call|start|payment)/;

const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; } };
const fresh = () => ({ sid: "web_" + Math.random().toString(36).slice(2) + Date.now().toString(36), messages: [], lead: null });

export const PublicAlly = () => {
  const { user } = useAuth();
  const { pathname, hash } = useLocation();
  const [opener, setOpener] = useState(() => getOpener(pathname, hash));
  const [s, setS] = useState(() => load() || fresh());
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [compact, setCompact] = useState(false);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(s)); }, [s]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [s.messages, open, streaming]);
  useEffect(() => {
    const show = () => setVisible(true);
    const onScroll = () => window.scrollY > 480 && show();
    const t = setTimeout(show, 9000);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(t); window.removeEventListener("scroll", onScroll); };
  }, []);
  useEffect(() => { if (visible) { const t = setTimeout(() => setCompact(true), 6000); return () => clearTimeout(t); } }, [visible]);

  useEffect(() => { if (!open) setOpener(getOpener(pathname, hash)); }, [pathname, hash, open]);

  if (user || PRIVATE.test(pathname)) return null;

  const openPanel = () => {
    if (!open && pathname === "/") {
      const el = document.getElementById("pricing");
      const r = el?.getBoundingClientRect();
      setOpener(getOpener(pathname, r && r.top < window.innerHeight * 0.6 && r.bottom > 0 ? "#pricing" : hash));
    }
    setOpen((o) => !o);
    setCompact(true);
  };

  const asked = s.asked ?? s.messages.filter((m) => m.role === "user").length;
  const gated = !s.lead && asked >= FREE && !streaming;
  const setLast = (content) => setS((p) => { const m = [...p.messages]; m[m.length - 1] = { ...m[m.length - 1], role: "assistant", content }; return { ...p, messages: m }; });

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming || gated) return;
    setInput("");
    const video = VIDEO_ASK.test(content) && !s.messages.some((m) => m.video);
    setS((p) => ({ ...p, asked: asked + 1, messages: [...p.messages, { role: "user", content }, { role: "assistant", content: "", video, demo: video }] }));
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: s.sid, message: content, public: true, page: `${opener.label} (${pathname})`, offer_video: video }) });
      if (res.status === 403) {
        setS((p) => ({ ...p, asked: FREE, messages: p.messages.slice(0, -2) }));
        setInput(content);
        return;
      }
      if (!res.ok) throw new Error(res.status === 429 ? "You've sent a lot of messages. Please try again in a little while." : "Sorry, I had trouble connecting. Please try again.");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setLast(acc);
      }
    } catch (e) { setLast(e.message); } finally { setStreaming(false); }
  };

  const onLead = (first) => setS((p) => ({ ...p, lead: first || "there", messages: [...p.messages, { role: "assistant", content: `Thanks, ${first || "there"}! Ask me anything else, or grab a quick demo with our team.`, demo: true }] }));

  const newChat = () => {
    if (streaming) return;
    fetch(`${API}/chat/${s.sid}/reset`, { method: "POST" }).catch(() => {});
    setS((p) => ({ ...p, asked, messages: [] }));
    setInput("");
  };

  return (
    <>
      <AnimatePresence>
        {(visible || open) && (
          <motion.button data-testid="public-ally-launcher" onClick={openPanel}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }} transition={{ duration: 0.3 }}
            aria-label={open ? "Close Ally" : "Ask Ally"}
            className="fixed bottom-5 right-5 z-[70] h-12 rounded-full bg-ally-ink text-white shadow-[0_10px_30px_-8px_rgba(255,0,131,0.55)] border border-white/10 flex items-center gap-2 px-3.5 hover:bg-ally-pink transition-colors">
            <span className="relative flex items-center justify-center w-5 h-5">
              {open ? <X size={18} /> : <Sparkles size={18} className="text-ally-pink" />}
              {!open && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-400 ring-2 ring-ally-ink" />}
            </span>
            {!open && !compact && <span className="hidden sm:inline text-sm font-semibold pr-1 whitespace-nowrap" data-testid="public-ally-pill-label">Ask Ally</span>}
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {open && (
          <motion.div data-testid="public-ally-panel"
            initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ duration: 0.2 }}
            className="fixed z-[70] bottom-20 right-3 left-3 sm:left-auto sm:right-5 sm:w-[370px] h-[min(540px,calc(100vh-7rem))] bg-white rounded-[24px] shadow-2xl border border-black/5 flex flex-col overflow-hidden">
            <div className="px-5 py-3.5 flex items-center gap-3 bg-ally-ink text-white">
              <div className="w-8 h-8 rounded-full bg-ally-pink flex items-center justify-center"><Sparkles size={15} /></div>
              <div>
                <div className="font-display font-bold leading-tight text-sm">Ally</div>
                <div className="text-[11px] text-white/60">DeferAlly's AI guide · usually instant</div>
              </div>
              {s.messages.length > 0 && (
                <button data-testid="public-ally-new-chat" onClick={newChat} disabled={streaming} title="Start a new chat"
                  className="ml-auto flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 transition-colors disabled:opacity-40">
                  <RotateCcw size={13} /> New chat
                </button>
              )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]">
              {s.messages.length === 0 && !gated && (
                <div className="mt-4">
                  <p className="text-ally-slate text-sm" data-testid="public-ally-greeting">{opener.greet}</p>
                  <div className="flex flex-col items-start gap-2 mt-4">
                    {opener.qs.map((q, i) => (
                      <button key={q} data-testid={`public-ally-suggest-${i}`} onClick={() => send(q)}
                        className="bg-white border border-ally-soft hover:border-ally-pink text-ally-ink text-xs font-medium rounded-full px-3 py-2 transition-colors">{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {s.messages.map((m, i) => (
                <div key={i} data-testid={`public-ally-msg-${m.role}-${i}`} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div className={m.role === "user" ? "bg-ally-pink text-white rounded-2xl rounded-br-md px-3.5 py-2 text-sm max-w-[85%]" : "bg-white border border-black/5 text-ally-ink rounded-2xl rounded-bl-md px-3.5 py-2 text-sm max-w-[90%]"}>
                    {m.content ? (m.role === "assistant" ? <RichText text={m.content} /> : m.content) : <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 bg-ally-pink/60 rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-ally-pink/60 rounded-full animate-bounce [animation-delay:0.15s]" /><span className="w-1.5 h-1.5 bg-ally-pink/60 rounded-full animate-bounce [animation-delay:0.3s]" /></span>}
                  </div>
                  {m.video && m.content && (
                    <video data-testid={`public-ally-video-${i}`} src="/vsl/DeferAlly-VSL-v3.mp4" poster="/vsl/DeferAlly-VSL-poster.jpg" controls playsInline preload="none"
                      className="mt-2 w-full max-w-[300px] aspect-video rounded-xl bg-black border border-black/10" />
                  )}
                  {m.demo && (
                    <Link to="/?demo=1" onClick={() => setOpen(false)} data-testid="public-ally-book-demo"
                      className="mt-2 inline-flex items-center gap-1.5 bg-ally-ink text-white text-xs font-semibold rounded-full px-3.5 py-2 hover:bg-ally-pink transition-colors">
                      <CalendarCheck size={13} /> Book a demo
                    </Link>
                  )}
                </div>
              ))}
              {gated && <AllyLeadCard sid={s.sid} page={`${opener.label} (${pathname})`} onDone={onLead} />}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-black/5 flex gap-2 bg-white">
              <input data-testid="public-ally-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={gated}
                placeholder={gated ? "Share your details above to continue" : "Ask about DeferAlly or 1031s…"}
                className="flex-1 bg-[#F5F5F7] rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ally-pink/30 disabled:opacity-60" />
              <button data-testid="public-ally-send" type="submit" disabled={streaming || gated || !input.trim()}
                className="w-10 h-10 rounded-full bg-ally-pink text-white flex items-center justify-center disabled:opacity-40 hover:bg-ally-ink transition-colors"><Send size={16} /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
